// Publish ▸ Adjust… (the Mac's FormatSettingsPanel; design-docs/Format settings — design.md): the format's settings as
// plain controls in place of the details, beside the preview. Each change is written to the project's own version of the
// format (`FormatAdjustment`, `adjusted-<built-in>` for a built-in) a moment after it's made, through the plan editor a
// line at a time, and the preview redraws. A dot marks a setting that differs from the format it starts from.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { OpenedProject } from '@writing-suite/core';
import type { LiveProject } from '../store/project.ts';
import { savedSearchesOf } from '@writing-suite/core/plan';
import {
  adjustedID, builtInOfAdjusted, CHAPTER_DROPS, CHAPTER_HEADINGS, END_MARKERS, FONTS, FormatAdjustment, formatSections, headerParts,
  headerText, headingExample, LINE_SPACINGS, MARGIN_LABELS, MARGINS, marginValues, PAGE_EDGE_ORNAMENTS, pageSizes, PART_HEADINGS, PLACEHOLDER_LABELS,
  PLACEHOLDERS, presetHasComments, presetScalar, PresetLibrary, projectPresetSources, RUNNING_HEADS, samePresetValue, SCENE_BREAKS,
  SECTION_TITLES, settingPaths, type Choice, type CompilePreset, type FormatSetting, type HeaderPart, type Placeholder, type PValue,
} from '@writing-suite/compile';
import { planEdit } from '../store/plan.ts';
import { announce } from '../shell/context.ts';

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function FormatSettings({ project, live, presetId, title, example, onDone, onSaved }: {
  project: OpenedProject;
  live: LiveProject | null;
  presetId: string;
  title: string;
  example: { number: number; title: string };
  onDone: () => void;
  /** A version was made, renamed or removed: its id (null: back to the built-in), to choose it. */
  onSaved: (presetId: string | null) => void;
}) {
  const own = projectPresetSources(project.record.settings);
  const [adjustment, setAdjustment] = useState<FormatAdjustment | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [hadComments, setHadComments] = useState(false);
  const [asText, setAsText] = useState<string | null>(null);
  const saving = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The version this preset is edited through: itself when it's the project's own, else the built-in's adjusted one.
  useEffect(() => {
    if (adjustment && (adjustment.id === presetId || adjustment.id === adjustedID(presetId))) return;
    try {
      const library = new PresetLibrary(own);
      const a = own[presetId] !== undefined
        ? FormatAdjustment.make(presetId, own[presetId], library.parent(presetId) ?? 'base', library)
        : FormatAdjustment.make(adjustedID(presetId), own[adjustedID(presetId)] ?? null, presetId, library);
      setAdjustment(a);
      setHadComments(presetHasComments(own[a.id] ?? ''));
      setHistory([]);
      setProblem(null);
    } catch (e) {
      setProblem(errorText(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId]);

  const preset: CompilePreset | null = useMemo(() => {
    try {
      return new PresetLibrary(own).preset(presetId);
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId, JSON.stringify(own)]);

  const isOwn = !!adjustment && builtInOfAdjusted(adjustment.id) === null;
  const parentName = (adjustment && typeof adjustment.inherited(['name']) === 'string' ? (adjustment.inherited(['name']) as string) : null) ?? preset?.name ?? title;
  const print = !!preset?.render.print;
  const writable = !!live?.writable;

  const write = (a: FormatAdjustment) => {
    if (!live) return;
    const removes = builtInOfAdjusted(a.id) !== null && !a.hasSettings;
    if (removes && own[a.id] === undefined) return;
    if (!removes) {
      try {
        new PresetLibrary({ ...own, [a.id]: a.yaml }).preset(a.id);
      } catch (e) {
        setProblem(`These settings couldn’t be read: ${errorText(e)}`);
        return;
      }
    }
    planEdit(live, (e) => e.setCompilePreset(a.id, removes ? null : a.yaml));
    setProblem(null);
    onSaved(removes ? null : a.id);
  };

  const change = (edit: (a: FormatAdjustment) => FormatAdjustment) => {
    if (!adjustment || !writable) return;
    const next = edit(adjustment);
    if (next.yaml === adjustment.yaml) return;
    setHistory((h) => [...h, adjustment.yaml]);
    setAdjustment(next);
    setHadComments(false);
    if (saving.current) clearTimeout(saving.current);
    saving.current = setTimeout(() => write(next), 400);
  };
  useEffect(() => () => { if (saving.current) clearTimeout(saving.current); }, []);

  const undo = () => {
    if (!adjustment || !history.length) return;
    const previous = history[history.length - 1];
    const restored = adjustment.withYAML(previous);
    setHistory((h) => h.slice(0, -1));
    setAdjustment(restored);
    if (saving.current) clearTimeout(saving.current);
    saving.current = setTimeout(() => write(restored), 400);
  };

  const duplicate = () => {
    if (!adjustment || !live) return;
    const name = window.prompt('Name the new format', `${title} 2`)?.trim();
    if (!name) return;
    const id = `custom-${crypto.randomUUID()}`;
    const text = adjustment.own.set(name, ['name']).set(`Based on ${parentName}.`, ['description']).set(adjustment.parentID, ['inherits']);
    planEdit(live, (e) => e.setCompilePreset(id, text.yaml));
    announce(`Made “${name}”, under Your formats`);
    onSaved(id);
  };

  const remove = () => {
    if (!adjustment || !live) return;
    if (!window.confirm(`Delete “${title}”? Files you’ve already built with it, and their checkpoints, are kept.`)) return;
    planEdit(live, (e) => e.setCompilePreset(adjustment.id, null));
    onSaved(null);
    onDone();
  };

  const saveText = (yaml: string) => {
    if (!adjustment || !live) return;
    try {
      new PresetLibrary({ ...own, [adjustment.id]: yaml }).preset(adjustment.id);
    } catch (e) {
      setProblem(`These settings couldn’t be read: ${errorText(e)}`);
      return;
    }
    planEdit(live, (e) => e.setCompilePreset(adjustment.id, yaml));
    setAdjustment(adjustment.withYAML(yaml));
    setAsText(null);
    onSaved(adjustment.id);
  };

  if (!preset || !adjustment) {
    return (
      <div className="format-settings">
        <h2 className="format-settings-title">Adjust “{title}”</h2>
        {problem ? <p className="error" role="alert">{problem}</p> : <p className="status-note">Reading the settings…</p>}
      </div>
    );
  }

  const get = (path: string[]) => adjustment.current(path);
  const bool = (path: string[]) => get(path) === true;
  const str = (path: string[]) => (typeof get(path) === 'string' ? (get(path) as string) : '');
  const num = (path: string[]) => (typeof get(path) === 'number' ? (get(path) as number) : 0);
  const strings = (path: string[]) => { const v = get(path); return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []; };

  const dot = (s: FormatSetting) => <ChangedDot setting={s} adjustment={adjustment} parentName={parentName} onReset={() => change((a) => a.reset(s))} />;
  const check = (s: FormatSetting, label: string, indent = false) => {
    const path = settingPaths(s)[0];
    return (
      <label className={`format-check${indent ? ' indent' : ''}`} key={s}>
        <input type="checkbox" checked={bool(path)} disabled={!writable} onChange={(e) => change((a) => a.set(e.target.checked, path))} />
        <span>{label}</span>
        {dot(s)}
      </label>
    );
  };
  const field = (s: FormatSetting, label: string, control: React.ReactNode, note?: string) => (
    <div className="format-field" key={s}>
      <div className="format-label">{label} {dot(s)}</div>
      {control}
      {note ? <p className="format-note">{note}</p> : null}
    </div>
  );
  function chips<V extends PValue>(choices: Choice<V>[], current: PValue | undefined, set: (v: V) => void, glyphs = false) {
    return (
      <div className={`format-chips${glyphs ? ' glyphs' : ''}`} role="group">
        {choices.map((c) => (
          <button type="button" key={String(c.value)} aria-pressed={samePresetValue(c.value, current)} disabled={!writable} onClick={() => set(c.value)}>
            {c.label}
          </button>
        ))}
      </div>
    );
  }
  const select = (choices: Choice<string>[], current: string, set: (v: string) => void, label: string) => (
    <select value={current} disabled={!writable} aria-label={label} onChange={(e) => set(e.target.value)}>
      {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      {!choices.some((c) => c.value === current) ? <option value={current}>{current}</option> : null}
    </select>
  );

  const statusNames = project.record.fields.find((f) => f.id === 'status')?.options.map((o) => o.name) ?? [];
  const collections = savedSearchesOf(project.record.settings).map((s) => s.name).filter(Boolean);
  const parentID = adjustment.parentID;
  const standardManuscript = parentID.startsWith('shunn') || parentID === 'blind-submission';

  const row = (s: FormatSetting): React.ReactNode => {
    const path = settingPaths(s)[0];
    switch (s) {
      case 'frontMatter': return check(s, 'Front matter (title page, copyright, dedication…)');
      case 'backMatter': return check(s, 'Back matter (acknowledgements, about the author…)');
      case 'respectIncludeInCompile': return check(s, 'Leave out documents with Include in Compile off');
      case 'excludedStatuses': {
        const chosen = adjustment.excludedStatuses;
        const on = (n: string) => chosen.some((c) => c.toLowerCase() === n.toLowerCase());
        return field(s, 'Leave out documents whose status is', statusNames.length ? (
          <div className="format-chips" role="group">
            {statusNames.map((n) => (
              <button type="button" key={n} aria-pressed={on(n)} disabled={!writable}
                onClick={() => change((a) => a.setExcludedStatuses(on(n) ? chosen.filter((c) => c.toLowerCase() !== n.toLowerCase()) : statusNames.filter((x) => x === n || on(x))))}>
                {n}
              </button>
            ))}
          </div>
        ) : <p className="format-note">This project has no statuses.</p>);
      }
      case 'onlyCollection': {
        const current = adjustment.onlyCollection ?? '';
        return field(s, 'Only the documents in', select([{ value: '', label: 'The whole manuscript' }, ...collections.map((c) => ({ value: c, label: c }))],
          current, (v) => change((a) => a.setOnlyCollection(v || null)), 'Only the documents in'));
      }
      case 'pageSize':
        return field(s, print ? 'Trim size' : 'Paper', print
          ? select(pageSizes(true), str(path), (v) => change((a) => a.set(v, path)), 'Trim size')
          : chips(pageSizes(false), get(path), (v) => change((a) => a.set(v, path))));
      case 'margins': {
        const current = adjustment.margins(print);
        return field(s, 'Margins', (
          <div className="format-chips" role="group">
            {MARGINS.map((m) => (
              <button type="button" key={m} aria-pressed={m === current} disabled={!writable} onClick={() => change((a) => a.setMargins(m, print))}>{MARGIN_LABELS[m]}</button>
            ))}
            {current === null ? <button type="button" aria-pressed="true" disabled>Your own</button> : null}
          </div>
        ), print ? 'Wider on the inside, by the spine.' : undefined);
      }
      case 'font': {
        const family = str(path), sizePath = settingPaths('fontSize')[0], size = num(sizePath);
        const expected = ['Times New Roman', 'Courier Prime', 'Courier', 'Courier New'].includes(family) && size === 12 && num(settingPaths('lineSpacing')[0]) === 2;
        return (
          <div className="format-field" key={s}>
            <div className="format-label">Font {dot('font')}{dot('fontSize')}</div>
            <div className="format-inline">
              {select(FONTS.map((f) => ({ value: f, label: f })), family, (v) => change((a) => a.set(v, path)), 'Font')}
              <Stepper value={size} step={0.5} min={8} max={16} unit=" pt" label="Font size" disabled={!writable} onChange={(v) => change((a) => a.set(v, sizePath))} />
            </div>
            {standardManuscript && !print && !expected ? <p className="format-note warn">Agents and editors expect Times New Roman or Courier, 12 pt, double-spaced.</p> : null}
          </div>
        );
      }
      case 'fontSize': return null;
      case 'lineSpacing': return field(s, 'Line spacing', chips(LINE_SPACINGS, get(path), (v) => change((a) => a.set(v, path))));
      case 'paragraphs': {
        const indent = adjustment.indent;
        return (
          <div className="format-field" key={s}>
            <div className="format-label">Paragraphs {dot(s)}</div>
            <div className="format-chips" role="group">
              <button type="button" aria-pressed={indent !== null} disabled={!writable} onClick={() => change((a) => a.indentParagraphs(preset.format === 'docx'))}>Indented</button>
              <button type="button" aria-pressed={indent === null} disabled={!writable} onClick={() => change((a) => a.setIndent(null))}>Space between</button>
            </div>
            {indent !== null ? (
              <div className="format-inline">
                <span className="format-note">Indent</span>
                <Stepper value={indent} step={0.05} min={0.1} max={1} unit=" in" label="Indent" disabled={!writable} onChange={(v) => change((a) => a.setIndent(Math.round(v * 100) / 100))} />
              </div>
            ) : null}
            {preset.format === 'epub' ? <p className="format-note">Readers choose the font and size on their own device, so an ebook doesn’t set them.</p> : null}
          </div>
        );
      }
      case 'justify': return check(s, 'Justified, with even right edges');
      case 'italicAsUnderline': return check(s, 'Show italics as underlining');
      case 'keepHighlights': return (
        <div className="format-field" key={s}>
          {check(s, 'Keep highlights and text colours')}
          <p className="format-note">For a copy you’re handing to an editor, who reads them as notes. Print and ebook formats never show them.</p>
        </div>
      );
      case 'chapterHeading':
      case 'partHeading': {
        const options = s === 'chapterHeading' ? CHAPTER_HEADINGS : PART_HEADINGS;
        const current = strings(path);
        const n = s === 'chapterHeading' ? example.number : 1, name = s === 'chapterHeading' ? example.title : 'The Weir';
        return field(s, s === 'chapterHeading' ? 'Chapter heading' : 'Part heading', (
          <div className="format-headings" role="group">
            {options.map((lines) => {
              const shown = headingExample(lines, n, name);
              return (
                <button type="button" key={lines.join('|')} aria-pressed={samePresetValue(lines, current)} disabled={!writable} onClick={() => change((a) => a.set(lines, path))}>
                  {shown.length > 1 ? <small>{shown[0]}</small> : null}
                  <span>{shown[shown.length - 1]}</span>
                </button>
              );
            })}
          </div>
        ), s === 'chapterHeading' ? 'Drawn with your first chapter. Chapters with Numbered off show their title alone.' : undefined);
      }
      case 'chapterStartsNewPage': return check(s, 'Start each chapter on a new page');
      case 'chapterOpensRecto': return check(s, 'Start each chapter on a right-hand page');
      case 'chapterDrop': return field(s, 'Space above a chapter heading', chips(CHAPTER_DROPS, get(path), (v) => change((a) => a.set(v, path))));
      case 'sceneBreak': return field(s, 'Between scenes', chips(SCENE_BREAKS, get(path), (v) => change((a) => a.set(v, path)), true));
      case 'titlePage': return check(s, 'A title page');
      case 'titlePageContact':
      case 'titlePageWordCount':
      case 'titlePageByline':
        if (!bool(settingPaths('titlePage')[0])) return null;
        return check(s, s === 'titlePageContact' ? 'Your name and contact details' : s === 'titlePageWordCount' ? 'The word count, rounded' : 'The byline', true);
      case 'header': {
        const parts = headerParts(str(path));
        const has = (p: string) => parts.some((x) => 'placeholder' in x && x.placeholder === p);
        const label = (x: HeaderPart) => ('placeholder' in x ? PLACEHOLDER_LABELS[x.placeholder] : x.text);
        return field(s, 'Header on every page', (
          <div className="format-chips header" role="group">
            {parts.map((x, i) => (
              <span className="format-token" key={i}>
                {label(x)}
                <button type="button" aria-label={`Remove ${label(x)}`} disabled={!writable}
                  onClick={() => change((a) => a.set(headerText(parts.filter((_, j) => j !== i)), path))}>×</button>
              </span>
            ))}
            {PLACEHOLDERS.some((p) => !has(p)) ? (
              <select value="" aria-label="Add to the header" disabled={!writable}
                onChange={(e) => e.target.value && change((a) => a.set(headerText([...parts, { placeholder: e.target.value as Placeholder }]), path))}>
                <option value="">+ Add</option>
                {PLACEHOLDERS.filter((p) => !has(p)).map((p) => <option key={p} value={p}>{PLACEHOLDER_LABELS[p]}</option>)}
              </select>
            ) : null}
          </div>
        ), preset.blind ? 'A blind submission never shows your name, whatever is here.' : undefined);
      }
      case 'endMarker': return field(s, 'At the end', chips(END_MARKERS, get(path), (v) => change((a) => a.set(v, path))));
      case 'versoHead': return field(s, 'Running head, left-hand pages', select(RUNNING_HEADS, str(path), (v) => change((a) => a.set(v, path)), 'Running head, left-hand pages'));
      case 'rectoHead': return field(s, 'Running head, right-hand pages', select(RUNNING_HEADS, str(path), (v) => change((a) => a.set(v, path)), 'Running head, right-hand pages'));
      case 'pageEdgeOrnament':
        return field(s, 'A blank-line scene break at a page’s foot or top', chips(PAGE_EDGE_ORNAMENTS, get(path), (v) => change((a) => a.set(v, path)), true),
          'A blank line there would be invisible, so it gets an ornament.');
      case 'preventWidows': return check(s, 'Never leave one line of a paragraph alone on a page');
      case 'balanceSpreads': return check(s, 'Even up facing pages');
    }
  };

  const count = adjustment.changed.length;
  return (
    <div className="format-settings" data-adjusting={adjustment.id}>
      <div className="publish-title">
        <h2 className="format-settings-title">Adjust “{title}”</h2>
        <p>{isOwn ? `Your own format, based on ${parentName}. Changes are saved as you make them.` : 'For this project only. Changes are saved as you make them.'}</p>
        {hadComments ? <p className="format-note warn">Changes here rewrite the settings as text and drop the notes written in it. Edit as Text… keeps them.</p> : null}
        {problem ? <p className="error" role="alert">{problem}</p> : null}
        {!writable ? <p className="status-note">The project is still opening.</p> : null}
      </div>
      {formatSections(preset).map(({ section, settings }) => (
        <section className="format-section" key={section} aria-label={SECTION_TITLES[section]}>
          <p className="eyebrow">{SECTION_TITLES[section]}</p>
          {settings.map(row)}
        </section>
      ))}
      <div className="format-foot">
        {isOwn ? (
          <button type="button" className="link-button danger" disabled={!writable} onClick={remove}>Delete “{title}”…</button>
        ) : (
          <button type="button" className="link-button" disabled={!writable || count === 0} onClick={() => change((a) => a.resetAll())}>
            Reset to “{parentName}”{count ? ` (${count} ${count === 1 ? 'change' : 'changes'})` : ''}
          </button>
        )}
        <div className="format-foot-links">
          <button type="button" className="link-button" disabled={!writable} onClick={duplicate}>Duplicate…</button>
          <button type="button" className="link-button" onClick={() => setAsText(own[adjustment.id] ?? adjustment.yaml)}>Edit as Text…</button>
          {history.length ? <button type="button" className="link-button" onClick={undo}>Undo</button> : null}
        </div>
        <button type="button" className="pill" onClick={onDone}>Done</button>
      </div>
      {asText !== null ? (
        <div className="format-text" role="group" aria-label="Settings as text">
          <p className="format-note">The settings as text, for things Adjust… doesn’t offer. Only what differs from “{parentName}” is written here; “inherits” names it. Help ▸ Compiling lists the settings you can use.</p>
          <textarea spellCheck={false} value={asText} onChange={(e) => setAsText(e.target.value)} aria-label="Settings as text" />
          <div className="format-foot-links">
            <button type="button" className="pill outline" onClick={() => setAsText(null)}>Cancel</button>
            <button type="button" className="pill" disabled={!writable} onClick={() => saveText(asText)}>Save</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Stepper({ value, step, min, max, unit, label, disabled, onChange }: {
  value: number; step: number; min: number; max: number; unit: string; label: string; disabled: boolean; onChange: (v: number) => void;
}) {
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v * 1000) / 1000));
  return (
    <span className="format-stepper" role="group" aria-label={label}>
      <button type="button" aria-label={`Less ${label.toLowerCase()}`} disabled={disabled || value <= min} onClick={() => onChange(clamp(value - step))}>−</button>
      <span>{presetScalar(value)}{unit}</span>
      <button type="button" aria-label={`More ${label.toLowerCase()}`} disabled={disabled || value >= max} onClick={() => onChange(clamp(value + step))}>+</button>
    </span>
  );
}

/** A dot beside a changed setting: what the format it starts from uses, and a way back to it. */
function ChangedDot({ setting, adjustment, parentName, onReset }: { setting: FormatSetting; adjustment: FormatAdjustment; parentName: string; onReset: () => void }) {
  const [open, setOpen] = useState(false);
  if (!adjustment.isChanged(setting)) return null;
  const inherited = describe(setting, adjustment);
  return (
    <span className="format-dot-wrap">
      <button type="button" className="format-dot" aria-label={`Changed. ${parentName} uses ${inherited}.`} aria-expanded={open} onClick={() => setOpen(!open)} />
      {open ? (
        <span className="format-dot-pop" role="dialog" aria-label="The format’s own setting">
          <span>{parentName} uses {inherited}.</span>
          <button type="button" className="pill" onClick={() => { setOpen(false); onReset(); }}>Use That</button>
        </span>
      ) : null}
    </span>
  );
}

function describe(s: FormatSetting, a: FormatAdjustment): string {
  const v = a.inherited(settingPaths(s)[0]);
  switch (s) {
    case 'excludedStatuses': {
      const names = (Array.isArray(v) ? v : []).flatMap((x) => (typeof x === 'object' && x && !Array.isArray(x) && typeof x.equals === 'string' ? [x.equals] : []));
      return names.length ? names.join(', ') : 'every status';
    }
    case 'onlyCollection': return 'the whole manuscript';
    case 'margins': {
      const print = a.inherited(['render', 'print']) !== undefined;
      const m = MARGINS.find((x) => Object.entries(marginValues(x, print)).every(([k, n]) => samePresetValue(a.inherited(['render', 'page', k]), n)));
      return m ? MARGIN_LABELS[m].toLowerCase() : 'its own margins';
    }
    case 'paragraphs': return samePresetValue(a.inherited(['render', 'firstLineIndent']), 0) ? 'space between paragraphs' : 'indented paragraphs';
    case 'chapterHeading':
    case 'partHeading': {
      const lines = Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
      return `“${headingExample(lines, 1, 'Title').join(' · ')}”`;
    }
    case 'header': {
      const parts = headerParts(typeof v === 'string' ? v : '');
      return parts.length ? parts.map((x) => ('placeholder' in x ? PLACEHOLDER_LABELS[x.placeholder] : x.text)).join(' / ') : 'no header';
    }
    case 'sceneBreak': case 'pageEdgeOrnament': case 'endMarker': case 'versoHead': case 'rectoHead':
      return typeof v === 'string' && v ? `“${v}”` : 'nothing';
    case 'fontSize': return `${v === undefined ? 12 : presetScalar(v)} pt`;
    case 'chapterDrop': return CHAPTER_DROPS.find((c) => samePresetValue(c.value, v))?.label.toLowerCase() ?? `${v === undefined ? 0 : presetScalar(v)} in`;
    case 'lineSpacing': return LINE_SPACINGS.find((c) => samePresetValue(c.value, v))?.label.toLowerCase() ?? (v === undefined ? '' : presetScalar(v));
    default:
      if (typeof v === 'boolean') return v ? 'on' : 'off';
      return v === undefined ? 'nothing' : presetScalar(v);
  }
}

